/**
 * Brings a newly arrived "Only visible to you" message into view (a slash command's or button's answer), like Slack:
 * to the end when it hangs off the newest row, else to the row it follows. Ephemerals already there when the
 * conversation opens don't scroll.
 */
import type { EphemeralMessage } from "@bundle/contracts";
import type { TimelineItem } from "@bundle/core";
import { newestEphemeralAnchor } from "@bundle/ui";
import { useEffect, useRef } from "react";

export function useEphemeralScroll(
  items: readonly TimelineItem[] | undefined,
  ephemerals: readonly EphemeralMessage[] | undefined,
  scrollTo: (key: string) => void,
): void {
  const newest = ephemerals?.at(-1)?.id;
  const seen = useRef(newest);
  useEffect(() => {
    if (newest === seen.current) return;
    seen.current = newest;
    if (newest === undefined || items === undefined || ephemerals === undefined) return;
    const key = newestEphemeralAnchor(items, ephemerals);
    if (key !== undefined) scrollTo(key);
    // Only a new ephemeral scrolls; later item changes must not.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [newest]);
}
